import React from 'react';
import { Medal, Star } from 'lucide-react';
import { FadeUp, ScalePop } from '../../../components/shared/Motion';
import { EditableText, EditableImage } from '../../components';

const MEDAL_GRADIENT = {
  gold: 'from-[#E8D1B9] via-[#C57863] to-[#A07B05]',
  silver: 'from-[#E8E8E8] via-[#B5B5B5] to-[#7A7A7A]',
  bronze: 'from-[#E8B27A] via-[#B07334] to-[#6E4720]'
};

function BoardResults({ data, onChange }) {
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const items = data.items || [];
  const updateItem = (i, patch) => {
    const next = items.map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    update({ items: next });
  };
  return (
    <section className="relative py-24 lg:py-28 bg-olive-deep text-white overflow-hidden">
      <div className="absolute inset-0 bg-grid-olive [background-size:42px_42px] opacity-25" />
      <div className="absolute -top-32 left-1/4 w-[420px] h-[420px] rounded-full bg-accent/18 blur-3xl" />
      <div className="absolute -bottom-32 right-1/4 w-[420px] h-[420px] rounded-full bg-olive-neon/18 blur-3xl" />

      <div className="container-page relative">
        <FadeUp className="text-center max-w-3xl mx-auto mb-14">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.28em] text-accent">
            <span className="h-px w-8 bg-accent" />
            <EditableText value={data.eyebrow} onChange={(t) => update({ eyebrow: t })} as="span" />
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.08]">
            <EditableText value={data.heading_prefix} onChange={(t) => update({ heading_prefix: t })} as="span" />
            <span className="text-gradient"><EditableText value={data.heading_highlight} onChange={(t) => update({ heading_highlight: t })} as="span" /></span>
            <EditableText value={data.heading_suffix} onChange={(t) => update({ heading_suffix: t })} as="span" />
          </h2>
          <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl mx-auto">
            <EditableText value={data.subheading} onChange={(t) => update({ subheading: t })} as="span" multiline />
          </p>
        </FadeUp>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
          {items.map((r, i) => (
            <ScalePop key={i} delay={i * 0.2}>
              <div className="relative bg-white text-ink rounded-2xl p-7 text-center shadow-[0_30px_60px_-25px_rgba(0,0,0,0.5)] border border-accent/10 hover:-translate-y-1.5 transition-all duration-500">
                <div className={`absolute -top-4 right-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br ${MEDAL_GRADIENT[r.medal] || MEDAL_GRADIENT.gold} text-white shadow-[0_10px_24px_-8px_rgba(0,0,0,0.5)] ring-4 ring-white`}>
                  <Medal size={20} />
                </div>
                <div className="relative mx-auto h-28 w-28 rounded-full overflow-hidden border-4 border-olive-light ring-2 ring-accent/40">
                  <EditableImage
                    src={r.image?.src}
                    alt={r.image?.alt || r.rank}
                    onChange={(src, alt) => updateItem(i, { image: { src, alt } })}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className={`mt-5 inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-[0.22em] text-ink bg-gradient-to-r ${MEDAL_GRADIENT[r.medal] || MEDAL_GRADIENT.gold} shadow`}>
                  <Star size={11} fill="currentColor" />
                  <EditableText value={r.rank} onChange={(t) => updateItem(i, { rank: t })} as="span" />
                </div>
                <EditableText value={r.name} onChange={(t) => updateItem(i, { name: t })} as="div" className="mt-4 font-display text-xl font-bold text-olive-deep" />
                <EditableText value={r.score} onChange={(t) => updateItem(i, { score: t })} as="div" className="mt-1 text-3xl font-display font-extrabold text-gradient" />
                <EditableText value={r.subject} onChange={(t) => updateItem(i, { subject: t })} as="div" className="mt-1 text-xs uppercase tracking-[0.22em] text-ink/55 font-semibold" />
              </div>
            </ScalePop>
          ))}
        </div>

        <FadeUp delay={0.6}>
          <p className="mt-10 text-center text-xs text-white/55 italic">
            <EditableText value={data.footnote} onChange={(t) => update({ footnote: t })} as="span" />
          </p>
        </FadeUp>
      </div>
    </section>
  );
}

export default BoardResults;
